import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiShield, FiUsers, FiBarChart3, FiInfo, FiLogOut, FiSettings, FiUserPlus, FiMenu, FiX, FiLogIn } = FiIcons;

const Header = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState(null);

  useEffect(() => {
    const authStatus = localStorage.getItem('isAuthenticated');
    const userData = localStorage.getItem('user');
    setIsAuthenticated(authStatus);
    setUser(userData ? JSON.parse(userData) : null);
  }, [location.pathname]);

  const navItems = [
    { path: '/', label: 'Home', icon: FiShield },
    { path: '/assessment', label: 'Assessment', icon: FiUsers },
    { path: '/dashboard', label: 'Dashboard', icon: FiBarChart3, protected: true },
    { path: '/about', label: 'About', icon: FiInfo }
  ];

  const handleLogout = () => {
    localStorage.removeItem('isAuthenticated');
    localStorage.removeItem('user');
    setIsAuthenticated(false);
    setUser(null);
    setIsMobileMenuOpen(false);
    navigate('/');
  };

  const handleNavLinkClick = () => {
    setIsMobileMenuOpen(false);
  };

  return (
    <header className="bg-white shadow-sm border-b border-gray-200 sticky top-0 z-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          {/* Logo */}
          <Link to="/" className="flex items-center space-x-2" onClick={handleNavLinkClick}>
            <SafeIcon icon={FiShield} className="h-8 w-8 text-blue-600" />
            <span className="text-xl font-bold text-gray-900">PsychSafety Pro</span>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex space-x-8">
            {navItems.map((item) => {
              if (item.protected && !isAuthenticated) return null;
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`flex items-center space-x-1 px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                    location.pathname === item.path
                      ? 'text-blue-600 bg-blue-50'
                      : 'text-gray-700 hover:text-blue-600 hover:bg-gray-50'
                  }`}
                >
                  <SafeIcon icon={item.icon} className="h-4 w-4" />
                  <span>{item.label}</span>
                </Link>
              );
            })}
            
            {/* Admin Section - Desktop */}
            {isAuthenticated && user?.role === 'admin' && (
              <Link
                to="/admin"
                className={`flex items-center space-x-1 px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                  location.pathname === '/admin'
                    ? 'text-blue-600 bg-blue-50'
                    : 'text-gray-700 hover:text-blue-600 hover:bg-gray-50'
                }`}
              >
                <SafeIcon icon={FiSettings} className="h-4 w-4" />
                <span>Admin</span>
              </Link>
            )}
            
            {/* Auth Buttons - Desktop */}
            {isAuthenticated ? (
              <button
                onClick={handleLogout}
                className="flex items-center space-x-1 px-3 py-2 rounded-md text-sm font-medium text-gray-700 hover:text-blue-600 hover:bg-gray-50 transition-colors"
              >
                <SafeIcon icon={FiLogOut} className="h-4 w-4" />
                <span>Logout</span>
              </button>
            ) : (
              location.pathname !== '/login' && (
                <>
                  <Link
                    to="/register"
                    className="flex items-center space-x-1 px-3 py-2 rounded-md text-sm font-medium text-green-600 bg-green-50 hover:bg-green-100 transition-colors"
                  >
                    <SafeIcon icon={FiUserPlus} className="h-4 w-4" />
                    <span>Register</span>
                  </Link>
                  <Link
                    to="/login"
                    className="flex items-center space-x-1 px-3 py-2 rounded-md text-sm font-medium text-blue-600 bg-blue-50 hover:bg-blue-100 transition-colors"
                  >
                    <span>Login</span>
                  </Link>
                </>
              )
            )}
          </nav>

          {/* Mobile menu button */}
          <div className="md:hidden">
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="inline-flex items-center justify-center p-2 rounded-md text-gray-700 hover:text-blue-600 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-blue-500 transition-colors"
              aria-expanded={isMobileMenuOpen}
            >
              <span className="sr-only">Open main menu</span>
              {isMobileMenuOpen ? (
                <SafeIcon icon={FiX} className="h-6 w-6" />
              ) : (
                <SafeIcon icon={FiMenu} className="h-6 w-6" />
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile menu */}
      <div className={`md:hidden transition-all duration-300 ease-in-out ${
        isMobileMenuOpen ? 'max-h-screen opacity-100' : 'max-h-0 opacity-0 overflow-hidden'
      }`}>
        <div className="px-2 pt-2 pb-3 space-y-1 bg-white border-t border-gray-200">
          {navItems.map((item) => {
            if (item.protected && !isAuthenticated) return null;
            return (
              <Link
                key={item.path}
                to={item.path}
                onClick={handleNavLinkClick}
                className={`flex items-center space-x-3 px-3 py-2 rounded-md text-base font-medium transition-colors ${
                  location.pathname === item.path
                    ? 'text-blue-600 bg-blue-50 border-l-4 border-blue-600'
                    : 'text-gray-700 hover:text-blue-600 hover:bg-gray-50'
                }`}
              >
                <SafeIcon icon={item.icon} className="h-5 w-5" />
                <span>{item.label}</span>
              </Link>
            );
          })}

          {/* Admin Section - Mobile */}
          {isAuthenticated && user?.role === 'admin' && (
            <Link
              to="/admin"
              onClick={handleNavLinkClick}
              className={`flex items-center space-x-3 px-3 py-2 rounded-md text-base font-medium transition-colors ${
                location.pathname === '/admin'
                  ? 'text-blue-600 bg-blue-50 border-l-4 border-blue-600'
                  : 'text-gray-700 hover:text-blue-600 hover:bg-gray-50'
              }`}
            >
              <SafeIcon icon={FiSettings} className="h-5 w-5" />
              <span>Admin</span>
            </Link>
          )}

          {/* Divider */}
          {isAuthenticated && <div className="border-t border-gray-200 my-2" />}

          {/* Auth Section - Mobile */}
          {isAuthenticated ? (
            <div className="space-y-1">
              <div className="px-3 py-2">
                <p className="text-sm text-gray-500">Signed in as:</p>
                <p className="text-sm font-medium text-gray-900">{user?.name}</p>
                <p className="text-xs text-gray-500">{user?.email}</p>
              </div>
              <button
                onClick={handleLogout}
                className="flex items-center space-x-3 w-full px-3 py-2 rounded-md text-base font-medium text-gray-700 hover:text-red-600 hover:bg-red-50 transition-colors"
              >
                <SafeIcon icon={FiLogOut} className="h-5 w-5" />
                <span>Logout</span>
              </button>
            </div>
          ) : (
            location.pathname !== '/login' && (
              <div className="space-y-1">
                <Link
                  to="/register"
                  onClick={handleNavLinkClick}
                  className="flex items-center space-x-3 px-3 py-2 rounded-md text-base font-medium text-green-600 bg-green-50 hover:bg-green-100 transition-colors"
                >
                  <SafeIcon icon={FiUserPlus} className="h-5 w-5" />
                  <span>Register</span>
                </Link>
                <Link
                  to="/login"
                  onClick={handleNavLinkClick}
                  className="flex items-center space-x-3 px-3 py-2 rounded-md text-base font-medium text-blue-600 bg-blue-50 hover:bg-blue-100 transition-colors"
                >
                  <SafeIcon icon={FiLogIn} className="h-5 w-5" />
                  <span>Login</span>
                </Link>
              </div>
            )
          )}
        </div>
      </div>
    </header>
  );
};

export default Header;